<template>
  <div
    v-loading="isLoading"
    class="cloud-intercom-page"
  >
    <!-- 撥打彈窗 -->
    <transition name="calling-dialog-slide">
      <CallingDialog
        v-if="showCallingDialog"
        :callee-username="callingCallee.username"
        :callee-display-name="callingCallee.displayName"
        :sip-client="sipClient"
        @close="showCallingDialog = false"
        @guest-token-expired="onCallingDialogGuestTokenExpired"
      />
    </transition>

    <!-- Dev-only SIP 偵錯按鈕 -->
    <button
      v-if="isDev"
      class="ci-debug-btn"
      @click="showLogDialog = true"
    >
      <i class="el-icon-data-analysis" />
      <span>{{ logEntries.length }}</span>
    </button>

    <!-- SIP Event Log 對話框 -->
    <el-dialog
      v-if="isDev"
      title="SIP 事件紀錄"
      :visible.sync="showLogDialog"
      width="95%"
      :append-to-body="true"
    >
      <p
        v-if="logEntries.length === 0"
        class="ci-log-empty"
      >
        尚無事件紀錄
      </p>
      <div
        v-else
        class="ci-log-table-wrap"
      >
        <table class="ci-log-table">
          <thead>
            <tr>
              <th>時間</th>
              <th>來源</th>
              <th>事件</th>
              <th>訊息</th>
              <th />
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="entry in logEntries"
              :key="entry.id"
            >
              <td class="ci-log-td--time">
                {{ entry.time }}
              </td>
              <td class="ci-log-td--source">
                {{ entry.source }}
              </td>
              <td class="ci-log-td--event">
                {{ entry.event }}
              </td>
              <td class="ci-log-td--message">
                {{ entry.message }}
              </td>
              <td>
                <button
                  class="ci-log-view-btn"
                  @click="selectedLogEntry = entry"
                >
                  檢視
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </el-dialog>

    <!-- Log 詳細資料對話框 -->
    <el-dialog
      v-if="isDev"
      title="事件資料"
      :visible="!!selectedLogEntry"
      width="95%"
      :append-to-body="true"
      @close="selectedLogEntry = null"
    >
      <template v-if="selectedLogEntry">
        <dl class="ci-log-detail-grid">
          <div class="ci-log-detail-row">
            <dt>時間</dt>
            <dd>{{ selectedLogEntry.time }}</dd>
          </div>
          <div class="ci-log-detail-row">
            <dt>來源</dt>
            <dd>{{ selectedLogEntry.source }}</dd>
          </div>
          <div class="ci-log-detail-row">
            <dt>事件</dt>
            <dd>{{ selectedLogEntry.event }}</dd>
          </div>
          <div class="ci-log-detail-row">
            <dt>訊息</dt>
            <dd>{{ selectedLogEntry.message }}</dd>
          </div>
          <div
            v-if="selectedLogEntry.description"
            class="ci-log-detail-row"
          >
            <dt>說明</dt>
            <dd>{{ selectedLogEntry.description }}</dd>
          </div>
        </dl>
        <pre class="ci-log-detail-data">{{ selectedLogEntry.jsonData }}</pre>
      </template>
    </el-dialog>

    <div class="ci-body" :class="{'no-callees': hasNoCallees}">
      <h1 v-if="!hasNoCallees" class="ci-body__title">
        {{ $t('Intercom.CloudIntercom.CalleesPage.Title') }}
      </h1>

      <!-- 無可撥打對象 -->
      <template v-if="!isLoading && hasNoCallees">
        <div class="ci-status-icon">
          <img src="@/assets/img/intercom/intercom@3x.webp" />
        </div>
        <p class="ci-body__message">
          {{ $t('Intercom.CloudIntercom.CalleesPage.NoCallees') }}
        </p>
      </template>

      <!-- 撥打對象列表 -->
      <template v-else>
        <ul class="ci-callee-list">
          <li
            v-for="item in calleeItems"
            :key="item.key"
            class="ci-callee-item"
          >
            <span class="ci-callee-item__name">{{ item.name }}</span>
            <button
              class="ci-callee-item__btn"
              :disabled="item.isDisabled"
              @click="call(item.callee)"
            >
              <img
                :src="item.isDisabled
                  ? require('@/assets/img/intercom/call-disabled@3x.webp')
                  : require('@/assets/img/intercom/call-enable@3x.webp')"
                alt=""
              />
            </button>
          </li>
        </ul>
      </template>
    </div>
  </div>
</template>

<script>
import { apiVoip } from '@/api/Voip'
import { createSipClient } from '@/js/utils/sipClient'
import dayjs from 'dayjs'
import CallingDialog from './components/CallingDialog.vue'

export default {
  name: 'CalleesPage',
  components: { CallingDialog },
  props: {
    guestToken: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      // 頁面載入狀態（GetCallees 期間）
      isLoading: false,
      // 是否有部分銷售合約（目前保留供未來授權邏輯使用）
      hasCallForwardingContract: false,
      // 軟體大門口機（Tablet）資料
      tablet: null,
      // 管理座機（Phone）列表
      phones: [],
      // 是否顯示全屏通話 Dialog
      showCallingDialog: false,
      // 當前撥打目標 { username, displayName }
      callingCallee: null,
      // 正在確認在線狀態的 Phone SubscriberId（用於按鈕 loading 狀態）
      loadingPhoneId: null,
      // SIP 客戶端實例（CalleesPage 負責生命週期，CallingDialog 透過 prop 取用）
      sipClient: null,
      // SIP WebSocket 是否已連線就緒
      isSipReady: false,
      // dev-only：SIP 事件紀錄（上限 500 筆）
      logSequence: 0,
      logEntries: [],
      showLogDialog: false,
      selectedLogEntry: null
    }
  },
  computed: {
    showTablet () {
      if (!this.tablet) return false
      // 有部分銷售合約且拜訪戶別未授權時，隱藏按鈕
      // if (this.hasCallForwardingContract && !this.tablet.IsAuthorized) return false
      return true
    },
    tabletDisplayName () {
      return (this.tablet && this.tablet.TabletNote) || ''
    },
    hasNoCallees () {
      return !this.showTablet && this.phones.length === 0
    },
    /**
     * 將 Tablet 與 Phones 合併為統一格式的列表，供 v-for 渲染。
     * 每筆包含：key（唯一識別）、name（顯示名稱）、callee（原始資料）、isDisabled（按鈕是否禁用）
     */
    calleeItems () {
      const items = []
      if (this.showTablet && this.tablet) {
        items.push({
          key: 'tablet',
          name: this.tabletDisplayName,
          callee: this.tablet,
          isDisabled: !this.isSipReady
        })
      }
      this.phones.forEach(phone => {
        items.push({
          key: phone.Username,
          name: phone.DisplayName,
          callee: phone,
          isDisabled: !this.isSipReady || this.loadingPhoneId === phone.SubscriberId
        })
      })
      return items
    },
    // 只有開發環境才顯示偵錯按鈕
    isDev () {
      return process.env.VUE_APP_ENV === 'dev'
    }
  },
  mounted () {
    // guestToken 為空代表 session 已失效，父層 checkContractStatus 會自動導回歡迎頁，此處不啟動任何請求
    if (!this.guestToken) return
    // 並行執行，各自處理錯誤，不互相阻塞
    this.fetchCallees()
    this.initSipClient()
  },
  beforeDestroy () {
    if (this.sipClient) {
      this.sipClient.destroy()
      this.sipClient = null
    }
    this.logEntries = []
  },
  methods: {
    /**
     * 預先取得 SIP 憑證並建立 WebSocket 連線，與 fetchCallees 並行執行。
     * 完成後設 isSipReady = true，解鎖通話按鈕；失敗則顯示系統錯誤 alert。
     */
    async initSipClient () {
      try {
        const res = await apiVoip.Community.GetSipConfig({ GuestToken: this.guestToken })
        const sipConfig = res.data
        this.sipClient = createSipClient({
          wsUrl: `${sipConfig.WsProtocol}://${sipConfig.Domain}:${sipConfig.Port}`,
          sipDomain: sipConfig.Domain,
          sipUsername: sipConfig.Username,
          guestToken: this.guestToken,
          onLog: this.isDev ? this.onSipLog : null
        })
        await this.sipClient.init()
        this.isSipReady = true
      } catch (err) {
        if (this.isGuestTokenExpired(err)) {
          this.handleGuestTokenExpired()
          return
        }
        this.showSystemErrorAlert()
      }
    },

    /**
     * 接收 sipClient 的 log entry 並推入 logEntries，上限 500 筆
     * @param {object} entry - { time, source, event, message, jsonData, description }
     */
    onSipLog (entry) {
      if (this.isDev) {
        try {
          const { jsonData, ...rest } = entry
          console.log(rest)
          console.log(JSON.parse(entry.jsonData))
          this.logEntries.push({ ...entry, id: ++this.logSequence })
          if (this.logEntries.length > 500) {
            this.logEntries.shift()
          }
        } catch (error) {
          console.log(error)
        }
      }
    },

    /**
     * 取得可撥打對象列表（Tablet + Phones），失敗時顯示「無可撥打對象」
     */
    async fetchCallees () {
      this.isLoading = true
      try {
        const res = await apiVoip.Community.GetCallees({ GuestToken: this.guestToken })
        this.hasCallForwardingContract = res.data.HasCallForwardingContract
        this.tablet = res.data.Tablet || null
        this.phones = res.data.Phones || []
      } catch (err) {
        if (this.isGuestTokenExpired(err)) {
          this.handleGuestTokenExpired()
          return
        }
        this.tablet = null
        this.phones = []
        this.showSystemErrorAlert()
      } finally {
        this.isLoading = false
      }
    },

    /**
     * 判斷 API 錯誤是否為 guestToken 無效、已過期或訪客已離開（ErrorCode 400008）。
     * 前端已有 30 分鐘前端倒數計時器，此方法作為後端驗證的補充層。
     * @param {Error} err
     * @returns {boolean}
     */
    isGuestTokenExpired (err) {
      return err?.response?.data?.ErrorCode === 400008
    },

    /**
     * SIP 通話中收到 X-Error-Code 400005（guestToken 失效）時，
     * 由 CallingDialog emit guest-token-expired 觸發此方法。
     * 先關閉彈窗再複用 handleGuestTokenExpired 顯示提示並導回歡迎頁。
     */
    onCallingDialogGuestTokenExpired () {
      this.showCallingDialog = false
      this.handleGuestTokenExpired()
    },

    /**
     * 顯示系統錯誤提示（SIP 初始化或 GetCallees 失敗），使用者確認後重新整理頁面。
     * 用 _errorAlertShowing 防止兩個錯誤同時觸發時重複顯示。
     */
    showSystemErrorAlert () {
      if (this._errorAlertShowing) return
      this._errorAlertShowing = true
      this.$alert(
        this.$t('Intercom.CloudIntercom.CalleesPage.SystemErrorMsg'),
        this.$t('Intercom.CloudIntercom.CalleesPage.SystemErrorTitle'),
        {
          confirmButtonText: this.$t('Intercom.CloudIntercom.CalleesPage.SystemErrorConfirm'),
          type: 'warning',
          showClose: false,
          roundButton: true,
          closeOnClickModal: false,
          customClass: 'ci-alert'
        }
      ).then(() => {
        this._errorAlertShowing = false
        if (this._isDestroyed) return
        if (!this.guestToken) {
          this.handleGuestTokenExpired()
          return
        }
        this.fetchCallees()
        this.initSipClient()
      }).catch(() => {})
    },

    /**
     * 顯示 guestToken 過期提示，使用者確認後 emit guest-token-expired 由父層導回歡迎頁。
     */
    handleGuestTokenExpired () {
      this.$alert(
        this.$t('Intercom.CloudIntercom.CalleesPage.GuestTokenExpiredMsg'),
        this.$t('Intercom.CloudIntercom.CalleesPage.GuestTokenExpiredTitle'),
        {
          confirmButtonText: this.$t('Intercom.CloudIntercom.Common.Confirm'),
          type: 'warning',
          showClose: false,
          roundButton: true,
          closeOnClickModal: false,
          customClass: 'ci-alert'
        }
      ).then(() => {
        this.$emit('guest-token-expired')
      }).catch(() => {})
    },

    /**
     * 發起通話
     * - 凭證過期時顯示提示並要求重新驗證
     * - Phone 類型先呼叫 GetSubscriberLocation 確認裝置在線，離線則阻止撥出
     * - 驗證通過後開啟 CallingDialog
     * @param {object} callee - GetCallees 回傳的單筆項目（Tablet 或 Phone）
     */
    async call (callee) {
      // Phone 硬體裝置需先確認是否在線
      if (callee.SubscriberId) {
        this.loadingPhoneId = callee.SubscriberId
        let isOnline = false
        try {
          const res = await apiVoip.Guest.GetSubscriberLocation(callee.SubscriberId, { GuestToken: this.guestToken })
          const location = res.data && res.data.Location
          isOnline = !!(location && dayjs().isBefore(dayjs(location.ExpiredAt)))
        } catch (err) {
          if (this.isGuestTokenExpired(err)) {
            this.handleGuestTokenExpired()
            return
          }
          // 其他錯誤：isOnline 維持 false，後續顯示離線提示
        } finally {
          this.loadingPhoneId = null
        }
        if (!isOnline) {
          this.$alert(
            this.$t('Intercom.CloudIntercom.CalleesPage.DeviceOfflineMsg'),
            this.$t('Intercom.CloudIntercom.CalleesPage.DeviceOfflineTitle'),
            {
              confirmButtonText: this.$t('Intercom.CloudIntercom.Common.Confirm'),
              type: 'warning',
              showClose: false,
              roundButton: true,
              closeOnClickModal: false,
              customClass: 'ci-alert'
            }
          ).catch(() => {})
          return
        }
      }

      // Tablet 的 Username 在 Intercom.Username，Phone 的在頂層 Username
      const username = (callee.Intercom && callee.Intercom.Username) || callee.Username
      this.callingCallee = {
        username,
        displayName: callee.DisplayName || callee.TabletNote || username
      }
      this.showCallingDialog = true
    }
  }
}
</script>

<style lang="scss" scoped>
@import './cloudIntercom';

.ci-body__message {
  font-size: map-get($fontSize,small);
  color: #666;
  text-align: center;
  margin: 16px 0 0;

  &--error {
    color: #e53935;
  }

  &--retry {
    cursor: pointer;
    text-decoration: underline;
  }
}

.ci-body {
  &.no-callees {
    align-items: center;
    padding-top: 56px;
  }

  &.no-callees &__message {
    font-size: map-get($fontSize,medium);
    color: #999;
    margin: 0;
  }
}

.ci-callee-list {
  list-style: none;
  padding: 0;
  margin: 32px 0 0;
  width: 100%;
}

.ci-callee-item {
  display: flex;
  align-items: center;
  padding: 28px 12px;
  border: 0;
  border-radius: 12px;
  margin-bottom: 12px;
  background-color: #f3f3f3;

  &--disabled {
    background-color: #f5f5f5;
  }

  &__name {
    flex: 1;
    font-size: map-get($fontSize,medium);
    font-weight: 600;
    color: #1f211f;
  }

  &__tag {
    font-size: map-get($fontSize,x-small);
    color: #999;
    margin-right: 8px;
  }

  &__btn {
    width: fit-content;
    height: fit-content;
    border: none;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
    overflow: hidden;
    background-color: transparent;

    &:disabled {
      cursor: not-allowed;
    }

    & img {
      width: 36px;
      height: 36px;
    }
  }
}

// Dev-only 偵錯 UI
.ci-debug-btn {
  position: fixed;
  bottom: 30px;
  left: 16px;
  z-index: 100;
  background: rgba(0, 0, 0, 0.55);
  border: none;
  border-radius: 20px;
  color: #fff;
  font-size: map-get($fontSize,x-small);
  padding: 6px 10px;
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;

  i { font-size: map-get($fontSize,small); }
}

.ci-log-empty {
  text-align: center;
  color: #999;
  padding: 24px 0;
  margin: 0;
}

.ci-log-table-wrap {
  overflow-x: auto;
  max-height: 55vh;
  overflow-y: auto;
}

.ci-log-table {
  width: 100%;
  border-collapse: collapse;
  font-size: map-get($fontSize,x-small);

  th,
  td {
    padding: 6px 8px;
    text-align: left;
    border-bottom: 1px solid #eee;
    vertical-align: top;
  }

  th { background: #f5f5f5; font-weight: 600; }
}

.ci-log-td--time { white-space: nowrap; color: #999; }
.ci-log-td--source { white-space: nowrap; }
.ci-log-td--event { white-space: nowrap; font-family: monospace; }
.ci-log-td--message { max-width: 180px; word-break: break-word; }

.ci-log-view-btn {
  background: #eee;
  border: none;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: map-get($fontSize,x-small);
  cursor: pointer;
  white-space: nowrap;
}

.ci-log-detail-grid {
  margin: 0 0 16px;
}

.ci-log-detail-row {
  display: grid;
  grid-template-columns: 48px 1fr;
  gap: 8px;
  padding: 4px 0;
  font-size: map-get($fontSize,x-small);
  border-bottom: 1px solid #f0f0f0;

  dt { color: #999; font-weight: 600; }
  dd { margin: 0; word-break: break-all; }
}

.ci-log-detail-data {
  background: #1e1e1e;
  color: #d4d4d4;
  font-size: map-get($fontSize,x-small);
  padding: 12px;
  border-radius: 8px;
  overflow-x: auto;
  max-height: 40vh;
  overflow-y: auto;
  white-space: pre;
  margin: 0;
}
</style>

<!-- transition class 作用於子元件根元素，不能在 scoped 內使用 -->
<style lang="scss">
.calling-dialog-slide-enter-active,
.calling-dialog-slide-leave-active {
  transition: transform 0.4s cubic-bezier(0.32, 0.72, 0, 1);
}

.calling-dialog-slide-enter,
.calling-dialog-slide-leave-to {
  transform: translateY(100%);
}
</style>
